import { arraysContainSameElements } from '@/utils/toolkit/array/arrays-contain-same-elements.utils';
import { Memoize } from '@/utils/toolkit/memoize.decorator';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { inject, Injectable } from '@angular/core';
import moment from 'moment';
import { combineLatest, type Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { BuildingIndicatorCode } from '../../constants/building-indicator.constants';
import type { LegacyBuildingsIndicatorsRequest } from '../../interfaces/BuildingIndicators.interface';
import type { ClassificationNodeId } from '../../interfaces/Classification.interface';
import { AttributesProvider } from '../attributes/attributes.provider';
import { BuildingsIndicatorsProvider } from '../buildings-indicators/buildings-indicators.provider';
import { API_DATE_FORMAT } from '../constants.api';
import { DashboardingProvider } from '../dashboarding.provider';
import { AdvizeoCoreScalesProvider } from './advizeo-core-scales.provider';
import {
    DEFAULT_SCALE_CATEGORY,
    type Scale,
    SCALE_CATEGORY_ADVIZEO_ATTRIBUTE_CODE,
    ScaleCode,
    type ScaleRange,
    type WidgetScaleConfig
} from './scales.interface';
@Injectable({
    providedIn: 'root'
})
export class ScalesProvider {
    private _advizeoCoreScalesProvider = inject(AdvizeoCoreScalesProvider);
    private _buildingsIndicatorsProvider = inject(BuildingsIndicatorsProvider);
    private _dashboardingProvider = inject(DashboardingProvider);
    private _attributesProvider = inject(AttributesProvider);

    getScaleValue$(
        scaleCode: ScaleCode,
        startDate: string,
        endDate: string,
        classificationNodeId: ClassificationNodeId
    ): Observable<number> {
        const buildingIndicator: BuildingIndicatorCode = this.getBuildingIndicatorCodeByScaleCode(scaleCode);
        if (!buildingIndicator) return of(0);

        const periodName$: Observable<string> = this._dashboardingProvider.getPeriods$().pipe(
            map(periods => {
                const period = periods
                    ? periods
                          .map(period => this._dashboardingProvider.applyNormalizePeriodTo12FullMonths(period))
                          .find(period => period.start === startDate && period.end === endDate)
                    : null;
                return period ? period.name : null;
            }),
            filter(isNotNullish),
            distinctUntilChanged()
        );

        const universeIds$ = this._buildingsIndicatorsProvider
            .getReferenceUniverseIdsByIndicatorCode$(buildingIndicator)
            .pipe(filter(Array.isArray), distinctUntilChanged(arraysContainSameElements));

        return combineLatest([periodName$, universeIds$]).pipe(
            map(([periodName, universeIds]) => {
                const dataRequest: LegacyBuildingsIndicatorsRequest = {
                    classificationNodeId,
                    universeIds,
                    year: Number(periodName),
                    indicators: [{ indicatorCode: buildingIndicator }]
                };
                return dataRequest;
            }),
            switchMap(dataRequest => this._buildingsIndicatorsProvider.fetchBuildingsIndicatorsData$(dataRequest)),
            map(([buildingIndicatorYear]) => {
                return Array.isArray(buildingIndicatorYear) ? buildingIndicatorYear.reduce((a, b) => a + b.value, 0) : null!;
            })
        );
    }

    getScaleRanges$(scaleCode: ScaleCode, classificationNodeId: ClassificationNodeId): Observable<ScaleRange[]> {
        return this._attributesProvider.getAttributeByAdvizeoCode$(SCALE_CATEGORY_ADVIZEO_ATTRIBUTE_CODE).pipe(
            switchMap(attribute =>
                isNotNullish(attribute)
                    ? this._attributesProvider.fetchAttributeValueSnapshot$(
                          classificationNodeId,
                          attribute.attributeId,
                          moment().format(API_DATE_FORMAT)
                      )
                    : of(null)
            ),
            switchMap(attribeValueSnapshot => {
                const scaleCategory: string = isNotNullish(attribeValueSnapshot)
                    ? (attribeValueSnapshot.value as string)
                    : DEFAULT_SCALE_CATEGORY;

                return this.getScaleByScaleCodeAndScaleCategory$(scaleCode, scaleCategory).pipe(map(scale => scale.scaleRanges));
            })
        );
    }

    getScaleByScaleCodeAndScaleCategory$(scaleCode: ScaleCode, scaleCategory: string): Observable<Scale> {
        return this.fetchScaleRanges$().pipe(
            map(scales =>
                isNotNullish(scales) ? scales.find(scale => scale.scaleCode === scaleCode && scale.scaleCategory === scaleCategory)! : null!
            )
        );
    }

    @Memoize(1500)
    private fetchScaleRanges$(): Observable<Scale[]> {
        return this._advizeoCoreScalesProvider.fetchScaleRanges$();
    }

    private getBuildingIndicatorCodeByScaleCode(scaleCode: ScaleCode): BuildingIndicatorCode {
        switch (scaleCode) {
            case ScaleCode.DPE:
                return BuildingIndicatorCode.PRIMARY_CONSUMPTION_BY_SURFACE;
            case ScaleCode.GES:
                return BuildingIndicatorCode.CO2_BY_SURFACE;
            default:
                return null!;
        }
    }

    // TODO ADV-5050 flag-1
    getScaleLabels(scaleCode: ScaleCode): WidgetScaleConfig {
        switch (scaleCode) {
            case ScaleCode.DPE:
                return {
                    widgetTitle: 'WIDGET.SCALE.DPE.WIDGET_TITLE',
                    widgetSubtitle: 'WIDGET.SCALE.DPE.WIDGET_SUBTITLE',
                    valueLabel: 'WIDGET.SCALE.DPE.VALUE_LABEL',
                    referenceLabel: 'WIDGET.SCALE.DPE.REFERENCE_LABEL',
                    minRangeLabel: 'WIDGET.SCALE.DPE.MIN_RANGE_LABEL',
                    maxRangeLabel: 'WIDGET.SCALE.DPE.MAX_RANGE_LABEL'
                };
            case ScaleCode.GES:
                return {
                    widgetTitle: 'WIDGET.SCALE.GES.WIDGET_TITLE',
                    widgetSubtitle: 'WIDGET.SCALE.GES.WIDGET_SUBTITLE',
                    valueLabel: 'WIDGET.SCALE.GES.VALUE_LABEL',
                    referenceLabel: 'WIDGET.SCALE.GES.REFERENCE_LABEL',
                    minRangeLabel: 'WIDGET.SCALE.GES.MIN_RANGE_LABEL',
                    maxRangeLabel: 'WIDGET.SCALE.GES.MAX_RANGE_LABEL'
                };
            default:
                return {
                    widgetTitle: '',
                    widgetSubtitle: '',
                    valueLabel: 'WIDGET.SCALE.DEFAULT.VALUE_LABEL',
                    referenceLabel: 'WIDGET.SCALE.DEFAULT.REFERENCE_LABEL',
                    minRangeLabel: '',
                    maxRangeLabel: ''
                };
        }
    }
}
